import React, { useState } from 'react';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Trash2, Plus } from 'lucide-react';
import { BodyParagraphData, BodyParagraphType } from '@/types/writing';
import { SlashTriggerContext } from '@/types/slash-commands';
import { cn } from '@/lib/utils';

interface UnifiedParagraphSectionProps {
  paragraphs: BodyParagraphData[];
  onParagraphChange: (idx: number, value: string) => void;
  onTypeChange?: (idx: number, type: BodyParagraphType) => void; // Optional for Task 1
  onAdd: () => void;
  onRemove: (idx: number) => void;
  isSlashMenuOpen: boolean;
  handleSlashTrigger: (context: SlashTriggerContext) => void;
  handleSlashFilterUpdate: (filterText: string) => void;
  handleSlashClose: () => void;
  handleTextareaKeyDown: (
    event: React.KeyboardEvent<HTMLTextAreaElement>
  ) => void;
  textareaClassName?: string;
  // Configuration props
  sectionType: 'body' | 'detail';
  showTypeSelection?: boolean; // Task 2 only
  placeholderPrefix?: string; // "Body paragraph" or "Detail paragraph"
  addButtonTooltip?: string; // "Add Body Paragraph" or "Add Detail Paragraph"
}

const typeLabels: Record<BodyParagraphType, string> = {
  'one-idea': 'One Idea',
  'two-idea': 'Two Ideas',
  'counter-argument': 'Counter Arg',
};

const allTypes = Object.keys(typeLabels) as BodyParagraphType[];

export function UnifiedParagraphSection({
  paragraphs,
  onParagraphChange,
  onTypeChange,
  onAdd,
  onRemove,
  isSlashMenuOpen,
  handleSlashTrigger,
  handleSlashFilterUpdate,
  handleSlashClose,
  handleTextareaKeyDown,
  textareaClassName,
  sectionType,
  showTypeSelection = false,
  placeholderPrefix = sectionType === 'body'
    ? 'Body paragraph'
    : sectionType === 'detail'
    ? 'Detail paragraph'
    : 'Paragraph',
  addButtonTooltip = sectionType === 'body'
    ? 'Add Body Paragraph'
    : sectionType === 'detail'
    ? 'Add Detail Paragraph'
    : 'Add Paragraph',
}: UnifiedParagraphSectionProps) {
  const [popoverOpenStates, setPopoverOpenStates] = useState<
    Record<number, boolean>
  >({});

  const handlePopoverOpenChange = (idx: number, open: boolean) => {
    setPopoverOpenStates((prev) => ({ ...prev, [idx]: open }));
  };

  const handleTextChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>,
    idx: number
  ) => {
    const textarea = e.target;
    const newValue = textarea.value;
    const cursorPosition = textarea.selectionStart;
    const prevValue = paragraphs[idx]?.content || '';

    onParagraphChange(idx, newValue);

    const typedChar = newValue.charAt(cursorPosition - 1);
    const textBeforeCursor = newValue.substring(0, cursorPosition);
    const slashIndex = textBeforeCursor.lastIndexOf('/');

    if (!isSlashMenuOpen && typedChar === '/') {
      const textBeforeSlash = newValue.substring(0, cursorPosition - 1);

      // Extract word before slash
      const lastSpaceIndex = textBeforeSlash.trimEnd().lastIndexOf(' ');
      const wordBefore =
        lastSpaceIndex === -1
          ? textBeforeSlash.trim()
          : textBeforeSlash.trimEnd().substring(lastSpaceIndex + 1);

      // Extract first sentence
      const firstSentenceMatch = newValue.match(/^.*?[.!?](\s+|$)/);
      const firstSentence = firstSentenceMatch
        ? firstSentenceMatch[0].trim()
        : newValue.trim();

      const currentType = paragraphs[idx]?.type || '';

      const context: SlashTriggerContext = {
        targetElement: textarea,
        currentParagraphIndex: idx,
        currentParagraphType: currentType,
        textBeforeTrigger: textBeforeSlash,
        wordBeforeTrigger: wordBefore || null,
        firstSentenceOfParagraph: firstSentence || null,
      };
      handleSlashTrigger(context);
    } else if (isSlashMenuOpen) {
      if (
        slashIndex === -1 ||
        typedChar === ' ' ||
        newValue.length < prevValue.length
      ) {
        handleSlashClose();
      } else {
        const filter = newValue.substring(slashIndex + 1, cursorPosition);
        handleSlashFilterUpdate(filter);
      }
    }
  };

  return (
    <TooltipProvider>
      <div className='relative group/section space-y-2'>
        {paragraphs.map((p, idx) => (
          <div key={idx} className='group/paragraph relative' tabIndex={-1}>
            {/* Type selection for Task 2 only */}
            {showTypeSelection && onTypeChange && (
              <div className='absolute top-1 left-1 flex items-center gap-2 z-10 h-6'>
                {p.type && (
                  <span className='text-xs text-muted-foreground font-medium px-2'>
                    {typeLabels[p.type]}
                  </span>
                )}
                <div className='flex items-center gap-1 opacity-0 group-hover/paragraph:opacity-100 group-focus-within/paragraph:opacity-100 transition-opacity duration-150'>
                  {allTypes
                    .filter((type) => type !== p.type)
                    .map((type) => (
                      <span
                        key={type}
                        onClick={() => onTypeChange(idx, type)}
                        className='text-xs text-muted-foreground hover:text-primary cursor-pointer px-2'
                        role='button'
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            onTypeChange(idx, type);
                          }
                        }}
                      >
                        {typeLabels[type]}
                      </span>
                    ))}
                </div>
              </div>
            )}

            <Card className='relative bg-background/80 shadow-none border border-input p-0 transition-colors duration-150 hover:bg-muted/60 focus-within:bg-muted/60 rounded-md'>
              <CardContent className={cn('p-0', showTypeSelection && 'pt-6')}>
                <Textarea
                  id={`${sectionType}-paragraph-${idx}`}
                  placeholder={`${placeholderPrefix} #${idx + 1}`}
                  value={p.content}
                  onChange={(e) => handleTextChange(e, idx)}
                  onKeyDown={handleTextareaKeyDown}
                  className={cn(
                    'min-h-[80px] resize-none border-none bg-transparent focus:ring-0 focus:outline-none text-base',
                    showTypeSelection ? 'px-2 pb-2' : 'px-3 pb-3',
                    textareaClassName
                  )}
                />
                {idx === paragraphs.length - 1 && (
                  <div className='absolute bottom-2 right-2 opacity-0 group-hover/paragraph:opacity-100 group-focus-within/paragraph:opacity-100 transition-opacity duration-150'>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant='outline'
                          size='icon'
                          onClick={onAdd}
                          className='rounded-full h-6 w-6 shadow-sm opacity-80 hover:opacity-100 focus:opacity-100 transition-opacity duration-150 p-0'
                        >
                          <Plus className='h-4 w-4' />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{addButtonTooltip}</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                )}
              </CardContent>
            </Card>

            <Popover
              open={popoverOpenStates[idx]}
              onOpenChange={(open) => handlePopoverOpenChange(idx, open)}
            >
              <PopoverTrigger asChild>
                <Button
                  variant='ghost'
                  size='icon'
                  className='absolute top-1 right-1 h-6 w-6 opacity-0 disabled:opacity-0 group-hover/paragraph:opacity-100 group-focus-within/paragraph:opacity-100 transition-opacity duration-150 text-destructive z-10'
                  disabled={paragraphs.length <= 2}
                >
                  <Trash2 className='h-4 w-4' />
                </Button>
              </PopoverTrigger>
              <PopoverContent className='w-auto p-2'>
                <div className='text-sm font-medium'>Remove paragraph?</div>
                <div className='flex gap-2 mt-2'>
                  <Button
                    variant='outline'
                    size='sm'
                    className='h-7'
                    onClick={() => handlePopoverOpenChange(idx, false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant='destructive'
                    size='sm'
                    className='h-7'
                    onClick={() => {
                      onRemove(idx);
                      handlePopoverOpenChange(idx, false);
                    }}
                  >
                    Remove
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        ))}
      </div>
    </TooltipProvider>
  );
}
